import React, { useCallback, useMemo, useEffect, useRef } from "react";
import { tempbyCity } from "../../../constants/csvToArrayData";
import { australiaStates, nZstates } from "../../../constants/states";
import { Box, Stack, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { FirstStepFormData } from "../../../schemas/validationSchemas";
import useResponsive from "../../../hooks/useResponsive";
import { FormSelectField } from "../../common";

export const LocationForm = () => {
  const { isXs, isSm, isMd, isLg } = useResponsive();
  const isMobile = isXs || isSm;
  const isTablet = isMd || isLg;

  const { control, watch, setValue } = useFormContext<FirstStepFormData>();

  const isUpdatingFromCountry = useRef(false);
  const [countryValue, stateValue, cityValue, postcodeValue] = watch([
    "country",
    "state",
    "city",
    "postcode",
  ]);

  const prevCityValue = useRef(cityValue);
  const prevPostcodeValue = useRef(postcodeValue);

  const handleCityChange = useCallback(
    (selectedCity: string) => {
      const filterState = countryValue === "New Zealand" ? "NZ" : stateValue;
      const cityData = tempbyCity.find(
        (item) => item.State === filterState && item.City === selectedCity
      );

      if (cityData) {
        setValue("postcode", cityData.Postcode);

        const temperatureData: { [key: number]: number } = {};
        for (let month = 1; month <= 12; month++) {
          temperatureData[month] = cityData[
            month.toString() as keyof typeof cityData
          ] as number;
        }

        setValue("temperatureData", temperatureData);
      }
    },
    [countryValue, stateValue, setValue]
  );

  const handlePostcodeChange = useCallback(
    (selectedPostcode: string) => {
      const filterState = countryValue === "New Zealand" ? "NZ" : stateValue;
      const postcodeData = tempbyCity.find(
        (item) =>
          item.State === filterState && item.Postcode === selectedPostcode
      );

      if (postcodeData) {
        setValue("city", postcodeData.City);

        const temperatureData: { [key: number]: number } = {};
        for (let month = 1; month <= 12; month++) {
          temperatureData[month] = postcodeData[
            month.toString() as keyof typeof postcodeData
          ] as number;
        }

        setValue("temperatureData", temperatureData);
      }
    },
    [countryValue, stateValue, setValue]
  );

  useEffect(() => {
    if (
      prevCityValue.current !== cityValue &&
      cityValue &&
      !isUpdatingFromCountry.current
    ) {
      handleCityChange(cityValue);
    }
    prevCityValue.current = cityValue;
  }, [cityValue, handleCityChange]);

  useEffect(() => {
    if (
      prevPostcodeValue.current !== postcodeValue &&
      postcodeValue &&
      !isUpdatingFromCountry.current
    ) {
      handlePostcodeChange(postcodeValue);
    }
    prevPostcodeValue.current = postcodeValue;
  }, [postcodeValue, handlePostcodeChange]);

  useEffect(() => {
    if (isUpdatingFromCountry.current) return;

    isUpdatingFromCountry.current = true;

    if (countryValue === "New Zealand") {
      setValue("state", "NZ");
      setValue("city", "Auckland");
      setValue("postcode", "1010");

      const aucklandData = tempbyCity.find(
        (item) => item.State === "NZ" && item.City === "Auckland"
      );
      if (aucklandData) {
        const temperatureData: { [key: number]: number } = {};
        for (let month = 1; month <= 12; month++) {
          temperatureData[month] = aucklandData[
            month.toString() as keyof typeof aucklandData
          ] as number;
        }
        setValue("temperatureData", temperatureData);
      }
    } else if (countryValue === "Australia") {
      setValue("state", "VIC");
      setValue("city", "Melbourne");
      setValue("postcode", "3000");

      const melbourneData = tempbyCity.find(
        (item) => item.State === "VIC" && item.City === "Melbourne"
      );
      if (melbourneData) {
        const temperatureData: { [key: number]: number } = {};
        for (let month = 1; month <= 12; month++) {
          temperatureData[month] = melbourneData[
            month.toString() as keyof typeof melbourneData
          ] as number;
        }
        setValue("temperatureData", temperatureData);
      }
    }

    setTimeout(() => {
      isUpdatingFromCountry.current = false;
    }, 0);
  }, [countryValue, setValue]);

  useEffect(() => {
    if (cityValue && stateValue && !isUpdatingFromCountry.current) {
      const filterState = countryValue === "New Zealand" ? "NZ" : stateValue;
      const cityData = tempbyCity.find(
        (item) => item.State === filterState && item.City === cityValue
      );

      if (cityData) {
        const temperatureData: { [key: number]: number } = {};
        for (let month = 1; month <= 12; month++) {
          temperatureData[month] = cityData[
            month.toString() as keyof typeof cityData
          ] as number;
        }
        setValue("temperatureData", temperatureData);
      }
    }
  }, [cityValue, stateValue, countryValue, setValue]);

  const postcodes = useMemo(() => {
    const filterState = countryValue === "New Zealand" ? "NZ" : stateValue;
    if (!filterState) return [];

    return tempbyCity
      .filter((item) => item.State === filterState)
      .map((item) => item.Postcode)
      .sort((a, b) => a.localeCompare(b));
  }, [countryValue, stateValue]);

  const states = useMemo(() => {
    const sortedStates =
      countryValue === "Australia" ? australiaStates : nZstates;
    return sortedStates.sort((a, b) => a.localeCompare(b));
  }, [countryValue]);

  const getStateCities = useCallback((country: string, state: string) => {
    const filterState = country === "New Zealand" ? "NZ" : state;
    return tempbyCity
      .filter((item) => item.State === filterState)
      .map((item) => item.City)
      .sort((a, b) => a.localeCompare(b));
  }, []);

  const countries = useMemo(() => ["Australia", "New Zealand"], []);

  const cities = useMemo(() => {
    if (countryValue === "New Zealand") {
      return getStateCities(countryValue, "NZ");
    }
    return getStateCities(countryValue, stateValue);
  }, [getStateCities, countryValue, stateValue]);

  return (
    <Box
      sx={{ background: "#ffd200" }}
      display={"flex"}
      alignItems={"center"}
      flexDirection={"column"}
      pt={isMobile ? 8 : 0}
      p={isMobile ? 2 : 4}
    >
      <Stack textAlign={"center"}>
        <Typography
          mt={isMobile ? 2 : 0}
          fontSize={24}
          textAlign={isMobile ? "start" : "center"}
        >
          Pool Geographical Location
        </Typography>
        <Typography
          mt={2}
          fontSize={16}
          textAlign={isMobile ? "start" : "center"}
        >
          Enter the correct information for accurate results based on your
          pool's general location. For more information, read our Privacy
          Policy.
        </Typography>
      </Stack>

      {!isMobile ? (
        <Box
          display={"flex"}
          flexDirection={isMobile ? "column" : isTablet ? "column" : "row"}
          alignItems={"center"}
          pt={4}
          justifyContent={"space-between"}
          gap={isMobile ? 4 : isTablet ? 5 : 20}
        >
          <Stack gap={2} width={"100%"}>
            <FormSelectField
              name="country"
              control={control}
              label="Country"
              helperText="Please select your Country"
              variant="standard"
              fullWidth
              options={countries.map((country) => ({
                value: country,
                label: country,
              }))}
            />

            {countryValue === "Australia" && (
              <FormSelectField
                name="state"
                control={control}
                label="State"
                helperText="Please select your State"
                variant="standard"
                fullWidth
                options={states.map((state) => ({
                  value: state,
                  label: state,
                }))}
              />
            )}

            <FormSelectField
              name="city"
              control={control}
              label="Closest City"
              helperText="Please select your City"
              variant="standard"
              fullWidth
              options={
                cities?.map((city) => ({ value: city, label: city })) || []
              }
            />

            <FormSelectField
              name="postcode"
              control={control}
              label="Postcode"
              helperText="Please select your postcode"
              variant="standard"
              fullWidth
              options={
                postcodes?.map((postcode) => ({
                  value: postcode,
                  label: postcode,
                })) || []
              }
            />
          </Stack>
          {!isMobile &&
            cityValue &&
            (countryValue === "Australia"
              ? stateValue
              : countryValue === "New Zealand") && (
              <iframe
                width={isMobile ? "100%" : "550px"}
                height="350px"
                title="Google Maps"
                loading="lazy"
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyArgZhQ0oL5JiXr0bwpXtRtIJ_hFwtXwwI
    &q=${cityValue}+${
                  countryValue === "New Zealand" ? "New Zealand" : stateValue
                }`}
              ></iframe>
            )}
        </Box>
      ) : (
        <Stack gap={2} width={"100%"}>
          <FormSelectField
            name="country"
            control={control}
            label="Country"
            helperText="Please select your Country"
            variant="standard"
            fullWidth
            options={countries.map((country) => ({
              value: country,
              label: country,
            }))}
          />

          {countryValue === "Australia" && (
            <FormSelectField
              name="state"
              control={control}
              label="State"
              helperText="Please select your State"
              variant="standard"
              fullWidth
              options={states.map((state) => ({ value: state, label: state }))}
            />
          )}

          <FormSelectField
            name="city"
            control={control}
            label="Closest City"
            helperText="Please select your City"
            variant="standard"
            fullWidth
            options={
              cities?.map((city) => ({ value: city, label: city })) || []
            }
          />

          <FormSelectField
            name="postcode"
            control={control}
            label="Postcode"
            helperText="Please select your postcode"
            variant="standard"
            fullWidth
            options={
              postcodes?.map((postcode) => ({
                value: postcode,
                label: postcode,
              })) || []
            }
          />
        </Stack>
      )}

      {isMobile &&
        cityValue &&
        (countryValue === "Australia"
          ? stateValue
          : countryValue === "New Zealand") && (
          <iframe
            width={isMobile ? "100%" : "550px"}
            height="350px"
            style={{ marginTop: "40px" }}
            title="Google Maps"
            loading="lazy"
            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyArgZhQ0oL5JiXr0bwpXtRtIJ_hFwtXwwI
    &q=${cityValue}+${
              countryValue === "New Zealand" ? "New Zealand" : stateValue
            }`}
          ></iframe>
        )}
    </Box>
  );
};
